// @flow
import { runInAction } from "mobx";
import { stringify } from "query-string";

import { json } from "../xhr.js";

import { User } from "./user.js";
import { PublishableImpl } from "./publishable.js";
import type { Content, Publishable, PositionItem } from "./types.js";
import Font from "./font.js";
import type { JSONContent, JSONFont } from "./jsontypes.js";
import { maybePrefix } from "./helpers.js";

type Options = {
	apiBase?: string,
	apiToken?: string,
	authKey?: string,
	locale?: string,
};

function NotFoundError(message, type = 404) {
	const error = new Error(message);
	// $FlowFixMe: yes, we want to create a new prop
	error.type = type;
	return error;
}

// publishers have a numeric id, publishables have a string based id.
const fallbackUrl = (id: string, page: int = 0, tags: Array<string> = []) => {
	let path = `/meta/v1/publishables/${id}/`;
	const query = [];
	if (tags.length) {
		query.push("tags=" + tags.join(","));
	}
	if (String(id) === String(parseInt(id, 10))) {
		path = `/meta/v1/publisher/${id}/`;
		if (page) {
			query.push("page=" + page);
		}
	}
	return path + (query.length ? "?" + query.join("&") : "");
};

const Id = (json: JSONContent) => json.lookupId || String(json.id);
const rId = (json: JSONContent) =>
	Id(json) +
	(json.tagfilter && json.tagfilter.length
		? "::" + json.tagfilter.join(",")
		: "");

class Api {
	_API_BASE: string = "https://pagestrip.com/";
	_fontByUrl: { [key: string]: ?Font } = Object.create(null);

	_resolveById: { [key: string]: ?Promise<?Content> } = Object.create(null);

	_unresolved: { [key: string]: ?string } = Object.create(null);
	_userById: { [key: string]: ?User } = Object.create(null);
	_publishableById: { [key: string]: ?PublishableImpl } = Object.create(null);

	_searchByQuery: { [key: string]: ?Promise<Array<PositionItem>> } =
		Object.create(null);

	locale: string;
	options: Options;

	constructor(options: Options = {}) {
		this._API_BASE = options.apiBase || this._API_BASE;
		if (
			!!/^https?:\/\/pagestrip\.com/.exec(this._API_BASE) &&
			IS_BROWSER && window.location.host !== "pagestrip.com"
		) {
			this._API_BASE = "https://sonic.pagestrip.com/";
		}
		this.options = options;
	}

	clearUser(id: string) {
		const clear = (map) =>
			Object.keys(this[map]).forEach((k) => {
				if (k === id || k.startsWith(id + "::")) {
					this[map][k] = undefined;
				}
			});
		clear("_userById");
		clear("_resolveById");
	}

	resolveSync(id: string, ignoreFilter: boolean = false): ?Content {
		const user = this._userById[id];
		if (user) {
			return user;
		}
		const publishable = this._publishableById[id];
		if (publishable) {
			// $FlowFixMe: https://github.com/facebook/flow/issues/2647
			return publishable;
		}

		if (ignoreFilter) {
			for (const key in this._userById) {
				if (key.startsWith(id + "::")) {
					return this._userById[key];
				}
			}

			for (const key in this._publishableById) {
				if (key.startsWith(id + "::")) {
					return this._publishableById[key];
				}
			}
		}
	}

	resolve(
		id: string,
		page: int,
		tags: Array<string> = [],
		additionalQuery: any = null
	): Promise<?Content> {
		tags = tags || [];
		const resolveId = id + (tags.length ? "::" + tags.join(",") : "");
		const loadId =
			(page ? id + "::" + page : id) +
			(tags.length ? "::" + tags.join(",") : "");
		let promise = this._resolveById[loadId];
		if (!promise) {
			const res = this.resolveSync(resolveId);
			if (res && res.setPage) {
				res.setPage(page || 1);
			}
			if (
				res &&
				res.loaded &&
				(!Array.isArray(res.loaded) || res.loaded[page || 1])
			) {
				promise = Promise.resolve(res);
			} else {
				const url = maybePrefix(
					(res ? res.url : this._unresolved[resolveId]) ||
						fallbackUrl(id, page, tags),
					this._API_BASE
				);
				const aq = additionalQuery
					? (~url.indexOf("?") ? "&" : "?") + stringify(additionalQuery)
					: "";
				promise = this._load(resolveId, url + aq).then((json) =>
					this.register(Object.assign(json, { tagfilter: tags }))
				);
			}
		}
		this._resolveById[loadId] = promise;
		return promise.then((result) => {
			if (result && result.setPage) {
				result.setPage(page || result.currentPage || 1);
			}
			return result;
		});
	}

	resolveContext(
		id: string,
		hierarchy: Array<string>,
		tags: Array<string> = []
	) {
		const url = maybePrefix(
			`/meta/v1/publishables/${id}/context/?in=${hierarchy.join("_")}${
				tags.length ? "&tags=" + tags.join(",") : ""
			}`,
			this._API_BASE
		);
		return this._load(`cntxt:${id}`, url);
	}

	_load(id: string, url: string): Promise<JSONContent> {
		let xhr;
		const { locale, apiToken, authKey } = this.options;
		const options = { onSend: (_xhr) => (xhr = _xhr), headers: {} };
		if (locale) {
			url += `${~url.indexOf("?") ? "&" : "?"}locale=${locale}`;
		}
		if (authKey) {
			url += `${~url.indexOf("?") ? "&" : "?"}auth_key=${authKey}`;
		}
		if (apiToken) {
			Object.assign(options.headers, { "X-API-Token": apiToken });
		}
		return json(url, options).then(
			(json: JSONContent) => {
				if (locale && xhr) {
					const cl = xhr.getResponseHeader("Content-Language");
					if (cl && cl !== locale) {
						runInAction("setLocale", () => (this.options.locale = cl));
					}
				}
				return json;
			},
			(e) => {
				let { xhr: { status, readyState } = {} } = e;
				let msg = `Sorry, but I couldn't find content with the id "${id}"`;
				if (status === 0 && readyState === 4) {
					status = 408;
					msg = `Sorry, but I couldn't load content with the id "${id}"`;
				}
				throw new NotFoundError(msg, status);
			}
		);
	}

	register(json: JSONContent): ?Content {
		const { url } = json;
		switch (json.type) {
			case "collection":
			case "article":
			case "link":
				return this._registerPublishable(json);
			case "publisher":
			case "user":
				return this._registerUser(json);
			default:
				this._unresolved[Id(json)] = url;
				return null;
		}
	}

	_registerPublishable(json: JSONContent): Publishable {
		const id = Id(json);
		const resolveId = rId(json);
		let publishable = this._publishableById[resolveId];
		if (!publishable) {
			publishable = this._publishableById[resolveId] = new PublishableImpl(
				this,
				id,
				json
			);
		}
		publishable.merge(json);
		// $FlowFixMe: https://github.com/facebook/flow/issues/2647
		return publishable;
	}
	_registerUser(json: JSONContent): User {
		const resolveId = rId(json);
		let user = this._userById[resolveId];
		if (!user) {
			user = this._userById[resolveId] = new User(this, String(json.id), json);
		}
		user.merge(json);
		return user;
	}

	loadRelated(publishable, count) {
		const url = maybePrefix(
			`/meta/v1/publishables/${publishable.id}/related/?page_size=${count}`,
			this._API_BASE
		);
		return json(url);
	}

	search(
		publisher: string,
		query: string,
		page: number = 1
	): Promise<Array<PositionItem>> {
		let search = this._searchByQuery[query + "?p=" + page];
		if (!search) {
			const { locale, apiToken, authKey } = this.options;
			let url = maybePrefix(
				`/meta/v1/search/` +
					`?publisher_id=${publisher}&q=${encodeURIComponent(
						query
					)}&page_size=12&page=${page}`,
				this._API_BASE
			);
			const options = { headers: {} };
			if (locale) {
				url += `&locale=${locale}`;
			}
			if (authKey) {
				url += `&auth_key=${authKey}`;
			}
			if (apiToken) {
				Object.assign(options.headers, { "X-API-Token": apiToken });
			}
			search = this._searchByQuery[query] = json(url, options).then((res) => {
				const out = res.results.map((res) => ({
					type: "publishable",
					content: this.register(res),
					options: { style: "tile" },
				}));

				if (res.next || res.previous) {
					out.push({
						type: "pagenav",
						content: query,
						contentType: "search",
						next: res.next,
						prev: res.previous,
						page: res.page,
					});
				}

				return out;
			});
		}
		return search;
	}

	topLiked(publisher: string, page: number = 1): Promise<Array<PositionItem>> {
		const { locale, apiToken, authKey } = this.options;
		let url = maybePrefix(
			`/meta/v1/search/` +
				`?publisher_id=${publisher}&page_size=12&ordering=-likes&page=${page}`,
			this._API_BASE
		);
		const options = { headers: {} };
		if (locale) {
			url += `&locale=${locale}`;
		}
		if (authKey) {
			url += `&auth_key=${authKey}`;
		}
		if (apiToken) {
			Object.assign(options.headers, { "X-API-Token": apiToken });
		}
		return json(url, options).then((res) => {
			const out = res.results.map((res) => ({
				type: "publishable",
				content: this.register(res),
				options: { style: "tile" },
			}));

			if (res.next || res.previous) {
				out.push({
					type: "pagenav",
					content: true,
					contentType: "topliked",
					next: res.next,
					prev: res.previous,
					page: res.page,
				});
			}

			return out;
		});
	}

	tagged(
		publisher: string,
		tags: Array<string>,
		page: number = 1
	): Promise<Array<PositionItem>> {
		const { locale, apiToken, authKey } = this.options;
		let url = maybePrefix(
			`/meta/v1/publishables/` +
				`?publisher_id=${publisher}&tags=${encodeURIComponent(
					tags.join("+")
				)}&page_size=12&page=${page}&type=article`,
			this._API_BASE
		);
		const options = { headers: {} };
		if (locale) {
			url += `&locale=${locale}`;
		}
		if (authKey) {
			url += `&auth_key=${authKey}`;
		}
		if (apiToken) {
			Object.assign(options.headers, { "X-API-Token": apiToken });
		}
		return json(url, options).then((res) => {
			const out = res.results.map((res) => ({
				type: "publishable",
				content: this.register(res),
				options: { style: "tile" },
			}));

			if (res.next || res.previous) {
				out.push({
					type: "pagenav",
					content: tags,
					contentType: "tagged",
					next: res.next,
					prev: res.previous,
					page: res.page,
				});
			}

			return out;
		});
	}

	_getFont(json: JSONFont, baseUrl: string): Font {
		const url = maybePrefix(json.path, baseUrl);
		const params = "?" + json.style + "&" + json.weight;
		let font = this._fontByUrl[url + params];
		if (!font) {
			font = this._fontByUrl[url + params] = new Font(url, json);
		}
		return font;
	}
}

export default Api;



// WEBPACK FOOTER //
// ./lib/api/index.js